import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const INITIAL_GOALS = [
  { id:1, name:'Monthly revenue target',     metric:'Revenue',      target:8000,  current:4050, unit:'$',  period:'Apr 2026', assignee:'Habeeb H.', status:'On track'  },
  { id:2, name:'New club activations',        metric:'Activations',  target:15,    current:8,    unit:'',   period:'Apr 2026', assignee:'Habeeb H.', status:'On track'  },
  { id:3, name:'Scout evaluations',           metric:'Evaluations',  target:150,   current:87,   unit:'',   period:'Apr 2026', assignee:'All scouts', status:'Behind'   },
  { id:4, name:'Email open rate',             metric:'Open rate',    target:65,    current:58,   unit:'%',  period:'Apr 2026', assignee:'Amira S.',  status:'Behind'    },
  { id:5, name:'Support ticket resolution',   metric:'Resolution',   target:95,    current:97,   unit:'%',  period:'Apr 2026', assignee:'David O.',  status:'Achieved'  },
  { id:6, name:'Q2 pipeline value',           metric:'Pipeline',     target:50000, current:12400,unit:'$',  period:'Q2 2026',  assignee:'Habeeb H.', status:'On track'  },
];

const PROGRESS_DATA = [
  { name:'Revenue',    progress:51 },
  { name:'Activations',progress:53 },
  { name:'Evaluations',progress:58 },
  { name:'Open rate',  progress:89 },
  { name:'Resolution', progress:100},
  { name:'Pipeline',   progress:25 },
];

const statusColor = s => ({ 'On track':'#A7F432', Achieved:'#4fc3f7', Behind:'#ff4d4d' })[s]||'#8a9a97';
const statusBadge = s => ({ 'On track':'badge badge-green', Achieved:'badge badge-blue', Behind:'badge badge-red' })[s]||'badge badge-gray';

export default function Goals({ toast }) {
  const [goals, setGoals]       = useState(INITIAL_GOALS);
  const [creating, setCreating] = useState(false);
  const [newGoal, setNewGoal]   = useState({ name:'', metric:'Revenue', target:'', unit:'$', period:'Apr 2026', assignee:'Habeeb H.' });

  const create = () => {
    if (!newGoal.name||!newGoal.target) { toast?.('Fill in name and target','warning'); return; }
    const g = { id:Date.now(), ...newGoal, target:Number(newGoal.target), current:0, status:'On track' };
    setGoals(prev=>[...prev,g]); setCreating(false); setNewGoal({ name:'', metric:'Revenue', target:'', unit:'$', period:'Apr 2026', assignee:'Habeeb H.' }); toast?.('Goal created');
  };

  const Tip = ({ active,payload,label }) => active&&payload?.length?(
    <div style={{ background:'#131B1B',border:'1px solid #2a3a3a',borderRadius:8,padding:'8px 12px',fontSize:12 }}>
      <p style={{ color:'var(--text-muted)',marginBottom:4 }}>{label}</p>
      {payload.map((p,i)=><p key={i} style={{ color:'var(--accent)' }}>{p.value}% attained</p>)}
    </div>
  ):null;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total goals</div><div className="stat-value">{goals.length}</div><div className="stat-sub">This period</div></div>
        <div className="stat-card"><div className="stat-label">On track</div><div className="stat-value" style={{ color:'#A7F432' }}>{goals.filter(g=>g.status==='On track').length}</div><div className="stat-sub stat-up">Progressing</div></div>
        <div className="stat-card"><div className="stat-label">Achieved</div><div className="stat-value" style={{ color:'#4fc3f7' }}>{goals.filter(g=>g.status==='Achieved').length}</div><div className="stat-sub stat-up">Completed</div></div>
        <div className="stat-card"><div className="stat-label">Behind</div><div className="stat-value" style={{ color:'#ff4d4d' }}>{goals.filter(g=>g.status==='Behind').length}</div><div className="stat-sub">Needs attention</div></div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Goal attainment overview</span></div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={PROGRESS_DATA} margin={{ top:4,right:4,left:-20,bottom:0 }}>
            <XAxis dataKey="name" tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false} domain={[0,100]} tickFormatter={v=>`${v}%`}/>
            <Tooltip content={<Tip />}/>
            <Bar dataKey="progress" name="Progress" radius={[4,4,0,0]}>
              {PROGRESS_DATA.map((d,i)=>(
                <rect key={i} fill={d.progress>=100?'#4fc3f7':d.progress>=50?'#A7F432':'#ff4d4d'}/>
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
        <h3 style={{ fontFamily:'var(--font-display)',fontSize:16,fontWeight:500,color:'var(--text-primary)' }}>All goals</h3>
        <button className="btn btn-primary" onClick={()=>setCreating(true)}>+ Create goal</button>
      </div>

      {creating&&(
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">New goal</span><button className="btn btn-ghost" style={{ fontSize:12 }} onClick={()=>setCreating(false)}>✕</button></div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,marginBottom:14 }}>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Goal name *</label><input className="search-input" value={newGoal.name} onChange={e=>setNewGoal(p=>({...p,name:e.target.value}))} placeholder="e.g. Q2 Revenue Target" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Metric</label><select className="search-input" value={newGoal.metric} onChange={e=>setNewGoal(p=>({...p,metric:e.target.value}))}>{['Revenue','Activations','Evaluations','Open rate','Calls','Deals','Resolution'].map(m=><option key={m}>{m}</option>)}</select></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Target *</label><input type="number" className="search-input" value={newGoal.target} onChange={e=>setNewGoal(p=>({...p,target:e.target.value}))} placeholder="e.g. 10000" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Unit</label><input className="search-input" value={newGoal.unit} onChange={e=>setNewGoal(p=>({...p,unit:e.target.value}))} placeholder="$ or % or blank" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Period</label><input className="search-input" value={newGoal.period} onChange={e=>setNewGoal(p=>({...p,period:e.target.value}))} placeholder="e.g. Q2 2026" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Assignee</label><select className="search-input" value={newGoal.assignee} onChange={e=>setNewGoal(p=>({...p,assignee:e.target.value}))}>{['Habeeb H.','Amira S.','David O.','All scouts','All reps'].map(a=><option key={a}>{a}</option>)}</select></div>
          </div>
          <div style={{ display:'flex',gap:8 }}><button className="btn btn-primary" onClick={create}>Create goal</button><button className="btn btn-ghost" onClick={()=>setCreating(false)}>Cancel</button></div>
        </div>
      )}

      <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
        {goals.map(g=>{
          const pct = Math.min(Math.round((g.current/g.target)*100),100);
          return (
            <div key={g.id} className="card">
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12 }}>
                <div>
                  <div style={{ fontWeight:500,fontSize:14,color:'var(--text-primary)',marginBottom:4 }}>{g.name}</div>
                  <div style={{ fontSize:12,color:'var(--text-dim)' }}>{g.metric} · {g.period} · {g.assignee}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <span className={statusBadge(g.status)}>{g.status}</span>
                  <div style={{ fontSize:18,fontWeight:700,color:statusColor(g.status),marginTop:4 }}>{g.unit}{g.current.toLocaleString()} <span style={{ fontSize:12,color:'var(--text-dim)',fontWeight:400 }}>/ {g.unit}{g.target.toLocaleString()}</span></div>
                </div>
              </div>
              <div style={{ height:8,background:'var(--border)',borderRadius:4,overflow:'hidden' }}>
                <div style={{ width:`${pct}%`,height:'100%',background:statusColor(g.status),borderRadius:4,transition:'width 0.3s' }}/>
              </div>
              <div style={{ display:'flex',justifyContent:'space-between',marginTop:6 }}>
                <span style={{ fontSize:11,color:'var(--text-dim)' }}>{pct}% attained</span>
                <span style={{ fontSize:11,color:'var(--text-dim)' }}>{g.target-g.current > 0 ? `${g.unit}${(g.target-g.current).toLocaleString()} remaining` : '✓ Target reached'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
